import { queryOptions } from "@tanstack/react-query";
import { userConfigService } from "./user-config.service";

// ==================== QUERY KEYS ====================

export const userConfigKeys = {
	// Base keys
	all: ["userConfig"] as const,

	// Configuration values for a user
	values: (userId: string) =>
		[...userConfigKeys.all, "values", userId] as const,

	// Configuration values filtered by prefix
	valuesByPrefix: (userId: string, prefix: string) =>
		[...userConfigKeys.all, "values", userId, "prefix", prefix] as const,

	// Single configuration value by key
	valueByKey: (userId: string, key: string) =>
		[...userConfigKeys.all, "values", userId, "key", key] as const,
} as const;

// ==================== USER CONFIG QUERY OPTIONS ====================

export const userConfigQueryOptions = {
	/**
	 * Get all configuration values for a user
	 */
	values: (userId: string) =>
		queryOptions({
			queryKey: userConfigKeys.values(userId),
			queryFn: () => userConfigService.getConfigurationValues(userId),
			staleTime: 5 * 60 * 1000, // 5 minutes
			gcTime: 10 * 60 * 1000, // 10 minutes
			enabled: !!userId,
		}),

	/**
	 * Get configuration values filtered by prefix
	 */
	valuesByPrefix: (userId: string, prefix: string) =>
		queryOptions({
			queryKey: userConfigKeys.valuesByPrefix(userId, prefix),
			queryFn: () =>
				userConfigService.getConfigurationValuesByPrefix(userId, prefix),
			staleTime: 5 * 60 * 1000, // 5 minutes
			gcTime: 10 * 60 * 1000, // 10 minutes
			enabled: !!userId && !!prefix,
		}),

	/**
	 * Get a single configuration value by key
	 */
	valueByKey: (userId: string, key: string) =>
		queryOptions({
			queryKey: userConfigKeys.valueByKey(userId, key),
			queryFn: () => userConfigService.getConfigurationValueByKey(userId, key),
			staleTime: 5 * 60 * 1000, // 5 minutes
			gcTime: 10 * 60 * 1000, // 10 minutes
			enabled: !!userId && !!key,
		}),
};

// ==================== CONVENIENCE EXPORTS ====================

/**
 * All user config query options grouped by category
 */
export const userConfigQueries = {
	config: userConfigQueryOptions,
} as const;

/**
 * Query key factory for easy invalidation
 */
export const userConfigQueryKeys = userConfigKeys;

// ==================== UTILITY FUNCTIONS ====================

/**
 * Helper to invalidate all user config-related queries
 */
export function getUserConfigInvalidationKeys(userId?: string) {
	if (userId) {
		return {
			all: userConfigKeys.all,
			values: userConfigKeys.values(userId),
		};
	}
	return {
		all: userConfigKeys.all,
	};
}
